import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  QaiButtonComponent,
  QaiDialogComponent,
  QaiFieldComponent,
  QaiInputComponent,
  QaiFloatingSelectComponent,
} from '@qai/ui';
import { ASSIGNMENT_NAME_ALREADY_EXISTS_STATUS } from 'src/app/services/self-serve/assignments.service';
import { SIMULATION_NAME_ALREADY_EXISTS_STATUS, AuthorService } from 'src/app/services/self-serve/author.service';
import { EntityActionType, EntityType } from 'src/app/utils/enums/self-serve/assignment-type.enum';
import { IModule } from 'src/app/interfaces/self-serve/responses/imodule';
import { IConfirmActionPayload } from 'src/app/interfaces/self-serve/requests/iconfirm-action-payload';
import { SelfServeLocalStorageUtil } from 'src/app/utils/local-storage.util';

@Component({
  selector: 'action-modal',
  standalone: true,
  templateUrl: './action-modal.component.html',
  styleUrls: ['./action-modal.component.scss'],
  imports: [
    FormsModule,
    CommonModule,
    QaiFieldComponent,
    QaiDialogComponent,
    QaiFieldComponent,
    QaiButtonComponent,
    QaiInputComponent,
    QaiFloatingSelectComponent,
  ],
})
export class ActionModalComponent {
  @ViewChild('actionDialog') qaiDialogRef?: QaiDialogComponent;

  @Input() open = false;
  @Input() entityType: EntityType = EntityType.SIMULATION;
  @Input() entityActionType: EntityActionType = EntityActionType.CREATE;
  @Input() originalName = '';
  @Input() networkRequestInProgress = false;
  @Input() errorStatus?: number;

  @Output() confirmAction = new EventEmitter<IConfirmActionPayload>();
  @Output() closeModal = new EventEmitter<void>();

  inputName = '';
  showError = false;
  duplicateError = false;
  moduleOptions: { label: string; value: string }[] = [];
  selectedModuleUuid: string = '';
  isLoadingModuleOptions: boolean = false;

  EntityActionType = EntityActionType;
  EntityType = EntityType;

  constructor(private authorService: AuthorService) {}

  loadProducts(): void {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();

    if (!accountUuid) {
      console.error('No account UUID found for loading module options');
      this.moduleOptions = [];
      return;
    }

    this.isLoadingModuleOptions = true;
    this.authorService.getModuleList(accountUuid).subscribe({
      next: (modules: IModule[]) => {
        this.moduleOptions = modules.map((module) => ({
          label: module.name,
          value: module.uuid,
        }));
        this.isLoadingModuleOptions = false;
      },
      error: (err: any) => {
        console.error('Failed to load module options:', err);
        this.moduleOptions = [];
        this.isLoadingModuleOptions = false;
      },
    });
  }

  onAddNewProduct(moduleName: string): void {
    if (!moduleName || moduleName.trim() === '') {
      return;
    }

    this.isLoadingModuleOptions = true;
    this.authorService.createModule(moduleName.trim()).subscribe({
      next: (newModule: IModule) => {
        this.moduleOptions.push({
          label: newModule.name,
          value: newModule.uuid,
        });
        this.selectedModuleUuid = newModule.uuid;
        this.isLoadingModuleOptions = false;
      },
      error: (err: any) => {
        console.error('Failed to create new module:', err);
        this.isLoadingModuleOptions = false;
      },
    });
  }

  onModuleSelectionChange(value: string | undefined): void {
    if (value) {
      this.selectedModuleUuid = value;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open']) {
      const isOpenNow = changes['open']?.currentValue;

      if (isOpenNow) {
        this.qaiDialogRef?.showModal();

        if (
          this.entityActionType === EntityActionType.DUPLICATE &&
          this.originalName
        ) {
          this.inputName = `Copy of - ${this.originalName}`;
          this.showError = false;
          this.duplicateError = false;
        } else {
          this.inputName = '';
          this.showError = this.entityActionType === EntityActionType.CREATE;
          this.duplicateError = false;
        }

        // Load products when modal opens (only for simulations)
        if (this.entityType === EntityType.SIMULATION) {
          this.selectedModuleUuid = '';
          this.loadProducts();
        }
      } else {
        this.qaiDialogRef?.close();
      }
    }

    if (changes['errorStatus']) {
      this.handleErrorStatusChange(changes['errorStatus'].currentValue);
    }
  }

  private handleErrorStatusChange(status?: number): void {
    if (
      (status === ASSIGNMENT_NAME_ALREADY_EXISTS_STATUS &&
      this.entityActionType === EntityActionType.DUPLICATE) || (status === SIMULATION_NAME_ALREADY_EXISTS_STATUS)
    ) {
      this.duplicateError = true;
      this.showError = false;
    } else if (status === ASSIGNMENT_NAME_ALREADY_EXISTS_STATUS) {
      this.showError = true;
      this.duplicateError = false;
    }
  }

  onInputChange(): void {
    this.showError = false;
    this.duplicateError = false;
  }

  handleConfirmClick() {
    if (this.networkRequestInProgress) {
      return;
    }

    if (this.inputName.trim()) {
      this.showError = false;
      this.duplicateError = false;
      const payload: IConfirmActionPayload = {
        name: this.inputName.trim(),
      };
      if (this.entityType === EntityType.SIMULATION && this.selectedModuleUuid) {
        payload.moduleUuid = this.selectedModuleUuid;
      }
      this.confirmAction.emit(payload);
    } else {
      this.showError = true;
      this.duplicateError = false;
    }
  }

  handleCloseClick() {
    this.closeModal.emit();
  }

  get modalTitle(): string {
    const action =
      this.entityActionType === EntityActionType.DUPLICATE
        ? 'Duplicate'
        : 'Create new';
    const type =
      this.entityType === EntityType.SIMULATION ? 'Simulation' : 'Assignment';
    return `${action} ${type}`;
  }

  get shouldShowError(): boolean {
    return this.showError || this.duplicateError;
  }

  get errorMessage(): string {
    if (this.duplicateError) {
      const entityName = this.entityType === EntityType.ASSIGNMENT ? 'assignment' : 'simulation';
      const article = this.entityType === EntityType.ASSIGNMENT ? 'An' : 'A';
      return `${article} ${entityName} with the same name already exists.`;
    }
    if (this.showError) {
      return 'Name is required.';
    }
    return '';
  }
}
